import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdminApi } from '../hooks/useAdminApi';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import EntityForm from '../components/EntityForm';
import JsonImportExport from '../components/JsonImportExport';

const columns = [
  { key: 'list', label: 'Name', type: 'text', sortable: true },
  { key: 'type', label: 'Type', type: 'badge', sortable: true },
  { key: 'energy', label: 'Energy', type: 'number', sortable: true },
  { key: 'rarity', label: 'Rarity', type: 'badge', sortable: true },
  { key: 'cost.amount', label: 'Cost', type: 'number', sortable: true },
  { key: 'cost.currency', label: 'Currency', type: 'text', sortable: true },
  { key: 'image_url', label: 'Image', type: 'thumbnail', sortable: false },
];

const formFields = [
  { key: 'list', label: 'Name', type: 'text', required: true },
  { key: 'type', label: 'Type', type: 'select', required: true, options: ['mech', 'weapon'] },
  { key: 'energy', label: 'Energy', type: 'number', required: true, min: 2, max: 32 },
  { key: 'rarity', label: 'Rarity', type: 'select', required: true, options: ['common', 'uncommon', 'rare', 'epic_a', 'epic_b', 'epic_c', 'legendary'] },
  { key: 'cost.amount', label: 'Cost Amount', type: 'number', required: true, min: 0 },
  { key: 'cost.currency', label: 'Cost Currency', type: 'select', required: true, options: ['Credits', 'A-Coins', 'free', 'Paid', 'Referral'] },
  { key: 'image_url', label: 'Image', type: 'image', uploadPath: 'mech_weapon_images', fileNameFrom: 'list', placeholder: '/mech_weapon_images/Name.png' },
];

const tableFilters = [
  { key: 'type', label: 'Type', options: ['mech', 'weapon'] },
  { key: 'rarity', label: 'Rarity', options: ['common', 'uncommon', 'rare', 'epic_a', 'epic_b', 'epic_c', 'legendary'] },
];

const AdminMechsPage = () => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const { post } = useAdminApi();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showImportExport, setShowImportExport] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const res = await fetch('/list.json');
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = () => {
    setEditItem(null);
    setEditIndex(-1);
    setShowForm(true);
  };

  const handleEdit = (row, index) => {
    setEditItem(row);
    setEditIndex(index);
    setShowForm(true);
  };

  const handleDuplicate = (row) => {
    setEditItem({ ...row, list: row.list + ' (Copy)' });
    setEditIndex(-1);
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editIndex >= 0) {
        await post('/admin-update-data.php', { file: 'list.json', action: 'edit', data: formData, item_index: editIndex });
      } else {
        await post('/admin-update-data.php', { file: 'list.json', action: 'add', data: formData });
      }
      setShowForm(false);
      loadData().catch(() => {});
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await post('/admin-update-data.php', { file: 'list.json', action: 'delete', item_index: deleteTarget.index });
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const handleImport = async (imported) => {
    try {
      await post('/admin-update-data.php', { file: 'list.json', action: 'update', data: imported });
      setShowImportExport(false);
      await loadData();
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
  };

  if (loading) return <div className={dark ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Mechs & Weapons</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowImportExport(true)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border ${dark ? 'border-[#333] text-gray-300 hover:bg-[#333]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Import/Export
          </button>
          <button onClick={handleAdd}
            className={`px-3 py-2 rounded-lg text-sm font-medium text-white ${dark ? 'bg-[#FF9800] hover:bg-[#F57C00]' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
            + Add New
          </button>
        </div>
      </div>

      {error && <div className="text-red-500 text-sm">{error}</div>}

      <DataTable
        columns={columns}
        data={data}
        filters={tableFilters}
        onEdit={handleEdit}
        onDelete={(row, index) => setDeleteTarget({ row, index })}
        onDuplicate={handleDuplicate}
      />

      <EntityForm
        open={showForm}
        title={editIndex >= 0 ? 'Edit Mech/Weapon' : 'Add Mech/Weapon'}
        fields={formFields}
        initialData={editItem}
        onSave={handleSave}
        onCancel={() => setShowForm(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Entry"
        message={`Delete "${deleteTarget?.row?.list}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <JsonImportExport
        open={showImportExport}
        title="Mechs & Weapons"
        data={data}
        onImport={handleImport}
        onClose={() => setShowImportExport(false)}
      />
    </div>
  );
};

export default AdminMechsPage;
